/**
 * ProfileMenu — Menu global compte type Google/Microsoft/Notion/Stripe.
 *
 * Avatar cliquable en haut à droite → dropdown avec :
 *   - En-tête (avatar + nom + email + pays)
 *   - Mon profil (/moi)
 *   - Paramètres & Sécurité (/security)
 *   - Historique des connexions (/security ancré)
 *   - Langue / Région (via LanguageSwitcher intégré)
 *   - Notifications
 *   - Abonnement (visible pour les rôles pertinents)
 *   - Aide & support
 *   - Se déconnecter
 *
 * Responsive : fonctionne identiquement sur web et mobile.
 */
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  User as UserIcon, Settings, ShieldCheck, History, Globe, Bell,
  CreditCard, LifeBuoy, LogOut, ChevronRight, Sparkles, IdCard, Crown,
} from "lucide-react";
import { useAuth } from "@/lib/auth";
import { ImpersonationModal, useCanImpersonate } from "@/components/Impersonation";
import { initials } from "@/lib/format";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
  DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent,
  DropdownMenuPortal,
} from "@/components/ui/dropdown-menu";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const COUNTRY_FLAGS = {
  CA: "🇨🇦", FR: "🇫🇷", BE: "🇧🇪", CH: "🇨🇭", US: "🇺🇸", GB: "🇬🇧",
  MA: "🇲🇦", SN: "🇸🇳", CI: "🇨🇮", CM: "🇨🇲", BR: "🇧🇷", AU: "🇦🇺",
};

export default function ProfileMenu({ compact = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unread, setUnread] = React.useState(0);
  const [impersonationOpen, setImpersonationOpen] = React.useState(false);
  const canImpersonate = useCanImpersonate();

  React.useEffect(() => {
    let mounted = true;
    const fetchUnread = async () => {
      try {
        const { default: api } = await import("@/lib/api");
        const res = await api.get("/notifications/unread-count");
        if (mounted) setUnread(res.data?.total || 0);
      } catch { /* silencieux */ }
    };
    fetchUnread();
    const it = setInterval(fetchUnread, 60000);
    return () => { mounted = false; clearInterval(it); };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const showSubscription = ["conseiller", "partner_admin", "partner_member", "admin"].includes(
    user?.role,
  );
  const flag = user?.country ? COUNTRY_FLAGS[user.country] || "🌍" : "🌍";

  return (
    <>
    <DropdownMenu>
      <DropdownMenuTrigger
        className="inline-flex items-center justify-center rounded-full outline-none focus:ring-2 focus:ring-primary/40 transition-all hover:scale-[1.04] relative min-h-[44px] min-w-[44px]"
        data-testid="profile-menu-trigger"
        aria-label="Menu du compte"
      >
        <Avatar className={`${compact ? "w-9 h-9" : "w-10 h-10"} border border-border/60 shadow-sm`}>
          {user?.picture && <AvatarImage src={user.picture} alt={user.name} />}
          <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
            {initials(user?.name || user?.email || "?")}
          </AvatarFallback>
        </Avatar>
        {unread > 0 && (
          <span
            className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold flex items-center justify-center ring-2 ring-background"
            data-testid="profile-menu-unread-badge"
            aria-label={`${unread} notifications non lues`}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        )}
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={8}
        className="w-72 rounded-2xl border-border/60 shadow-xl p-2"
        data-testid="profile-menu-content"
      >
        {/* Header */}
        <div className="px-3 py-3 flex items-start gap-3">
          <Avatar className="w-11 h-11 border border-border/60">
            {user?.picture && <AvatarImage src={user.picture} alt={user.name} />}
            <AvatarFallback className="bg-primary/10 text-primary text-base font-medium">
              {initials(user?.name || user?.email || "?")}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate" data-testid="profile-menu-name">
              {user?.name || "—"}
            </div>
            <div className="text-xs text-muted-foreground truncate" data-testid="profile-menu-email">
              {user?.email}
            </div>
            <div className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-wider text-muted-foreground">
              <span className="text-sm leading-none">{flag}</span>
              <span>{user?.country || "—"}</span>
              <span className="opacity-40">·</span>
              <span>{user?.role || "user"}</span>
            </div>
          </div>
        </div>

        <DropdownMenuSeparator />

        {/* Mon profil */}
        <DropdownMenuItem asChild data-testid="profile-menu-my-profile">
          <Link to="/moi" className="flex items-center gap-3 cursor-pointer rounded-lg">
            <UserIcon className="w-4 h-4 text-muted-foreground" />
            <span className="flex-1">Mon profil</span>
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
          </Link>
        </DropdownMenuItem>

        {/* Type de dossier / profil */}
        <DropdownMenuItem asChild data-testid="profile-menu-profile-type">
          <Link to="/profile-type" className="flex items-center gap-3 cursor-pointer rounded-lg">
            <IdCard className="w-4 h-4 text-muted-foreground" />
            <div className="flex-1">
              <div>Type de dossier</div>
              <div className="text-[10px] text-muted-foreground">Adapte les sections à ton profil</div>
            </div>
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
          </Link>
        </DropdownMenuItem>

        {/* Basculer de rôle — fondateur uniquement */}
        {canImpersonate && (
          <DropdownMenuItem
            onSelect={(e) => { e.preventDefault(); setImpersonationOpen(true); }}
            className="flex items-center gap-3 cursor-pointer rounded-lg"
            data-testid="profile-menu-impersonate"
          >
            <Crown className="w-4 h-4 text-amber-600" />
            <div className="flex-1">
              <div>Basculer de rôle</div>
              <div className="text-[10px] text-muted-foreground">Émule les permissions d'un autre rôle</div>
            </div>
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
          </DropdownMenuItem>
        )}

        {/* Paramètres */}
        <DropdownMenuItem asChild data-testid="profile-menu-settings">
          <Link to="/security" className="flex items-center gap-3 cursor-pointer rounded-lg">
            <Settings className="w-4 h-4 text-muted-foreground" />
            <span className="flex-1">Paramètres</span>
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
          </Link>
        </DropdownMenuItem>

        {/* Sécurité */}
        <DropdownMenuItem asChild data-testid="profile-menu-security">
          <Link to="/security" className="flex items-center gap-3 cursor-pointer rounded-lg">
            <ShieldCheck className="w-4 h-4 text-muted-foreground" />
            <div className="flex-1">
              <div>Sécurité</div>
              <div className="text-[10px] text-muted-foreground">Mot de passe · 2FA · Sessions</div>
            </div>
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
          </Link>
        </DropdownMenuItem>

        {/* Historique des connexions */}
        <DropdownMenuItem asChild data-testid="profile-menu-history">
          <Link to="/security#sessions" className="flex items-center gap-3 cursor-pointer rounded-lg">
            <History className="w-4 h-4 text-muted-foreground" />
            <span className="flex-1">Historique des connexions</span>
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
          </Link>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Langue / Région — sous-menu */}
        <DropdownMenuSub>
          <DropdownMenuSubTrigger data-testid="profile-menu-language" className="rounded-lg">
            <Globe className="w-4 h-4 text-muted-foreground mr-3" />
            <span>Langue / Région</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent
              className="rounded-2xl p-3 min-w-[220px]"
              data-testid="profile-menu-language-content"
            >
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 px-1">
                Interface
              </div>
              <LanguageSwitcher variant="inline" />
              <div className="mt-3 pt-3 border-t border-border/40 text-[11px] text-muted-foreground">
                Pays actuel : <span className="font-medium">{flag} {user?.country || "—"}</span>
              </div>
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>

        {/* Notifications */}
        <DropdownMenuItem asChild data-testid="profile-menu-notifications">
          <Link to="/notifications" className="flex items-center gap-3 cursor-pointer rounded-lg">
            <Bell className="w-4 h-4 text-muted-foreground" />
            <span className="flex-1">Notifications</span>
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
          </Link>
        </DropdownMenuItem>

        {/* Abonnement (conditionnel) */}
        {showSubscription && (
          <DropdownMenuItem asChild data-testid="profile-menu-subscription">
            <Link to="/billing" className="flex items-center gap-3 cursor-pointer rounded-lg">
              <CreditCard className="w-4 h-4 text-muted-foreground" />
              <div className="flex-1">
                <div>Abonnement</div>
                <div className="text-[10px] text-muted-foreground">Plan & facturation</div>
              </div>
              <ChevronRight className="w-3.5 h-3.5 text-muted-foreground" />
            </Link>
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        {/* Aide */}
        <DropdownMenuItem asChild data-testid="profile-menu-help">
          <a
            href="mailto:support@elia.finance?subject=Aide%20ELIA%20Finance"
            className="flex items-center gap-3 cursor-pointer rounded-lg"
          >
            <LifeBuoy className="w-4 h-4 text-muted-foreground" />
            <span className="flex-1">Aide & support</span>
          </a>
        </DropdownMenuItem>

        {/* Nouveautés (petit lien discret) */}
        <DropdownMenuItem asChild>
          <Link to="/smart-docs" className="flex items-center gap-3 cursor-pointer rounded-lg">
            <Sparkles className="w-4 h-4 text-primary" />
            <span className="flex-1 text-primary">Nouveauté : Docs intelligents</span>
          </Link>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Déconnexion */}
        <DropdownMenuItem
          onSelect={(e) => { e.preventDefault(); handleLogout(); }}
          data-testid="profile-menu-logout"
          className="flex items-center gap-3 cursor-pointer rounded-lg text-rose-600 hover:!text-rose-700 hover:!bg-rose-50 focus:!text-rose-700 focus:!bg-rose-50"
        >
          <LogOut className="w-4 h-4" />
          <span className="flex-1">Se déconnecter</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
    <ImpersonationModal open={impersonationOpen} onOpenChange={setImpersonationOpen} />
    </>
  );
}
